import { type InventoryColumnDetails } from '@/features/inventory/admin/inventory-table-columns'
import {
  CheckCell,
  DateCell,
  ImageCell,
  TableCell,
  TableRow,
} from '@/components/Table'
import { Icon } from '@/components/Icon'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Fragment } from 'react'
import { keyBy, mapValues } from 'lodash-es'
import { IconButton } from '@/components/IconButton'
import { dateFormatters } from '@/utils/date-util'
import type {
  InventoryItemPriceDisplay,
  InventoryItemProgressionPriceDisplay,
} from '@/features/inventory/inventory-item-display'
import styles from './InventoryRow.module.css'

function formatDate(date?: number | Date) {
  const parts = dateFormatters.dateLong.formatToParts(date)
  const partsMap = mapValues(keyBy(parts, 'type'), 'value')

  return `${partsMap.day} ${partsMap.month} ${partsMap.year}`
}

export type InventoryRowColumn = Pick<InventoryColumnDetails, 'id' | 'align'>

export interface InventoryRowProps {
  columns: InventoryRowColumn[]
  id: string
  name: string
  shortId: string
  override: boolean
  global: boolean
  hidden: boolean
  createdByName: string
  createdByAvatar?: string | null
  createdByAvatarFallback?: string | null
  serviceTypeText: string
  version: number
  dateCreated: Date
  courtesyAmount: number
  priceTypeText: string
  supportingItemsCount: number
  clientPrice: InventoryItemPriceDisplay
  proPrice: InventoryItemPriceDisplay
  hasActions: boolean
  canEdit: boolean
  canOverride: boolean
  canArchive: boolean
  canUnarchive: boolean
  hasWorkspace: boolean
  onEdit: () => void
  onOverride: () => void
  onArchive: () => void
  onUnarchive: () => void
  actionsTestIds?: InventoryRowActionsTestIds
  testId?: string
}

/**
 * Test IDs of a row's action menu and its items.
 */
export interface InventoryRowActionsTestIds {
  menu: string
  update: string
  override: string
  archive: string
  unarchive: string
}

export function InventoryRow({
  columns,
  id,
  name,
  shortId,
  override,
  global,
  hidden,
  createdByName,
  createdByAvatar,
  createdByAvatarFallback,
  serviceTypeText,
  version,
  dateCreated,
  courtesyAmount,
  priceTypeText,
  supportingItemsCount,
  clientPrice,
  proPrice,
  hasActions,
  canEdit,
  canOverride,
  canArchive,
  canUnarchive,
  hasWorkspace,
  onEdit,
  onOverride,
  onArchive,
  onUnarchive,
  actionsTestIds,
  testId,
}: InventoryRowProps) {
  return (
    <TableRow data-test={testId}>
      {columns.map((column) => {
        switch (column.id) {
          case 'ServiceItem':
            return (
              <WorkItemCell
                name={name}
                shortId={shortId}
                override={override}
                key={column.id}
              />
            )
          case 'Global':
            return (
              <CheckCell
                checked={global}
                color="var(--blue400)"
                key={column.id}
              />
            )
          case 'Hidden':
            return (
              <CheckCell
                checked={hidden}
                color="var(--blue400)"
                key={column.id}
              />
            )
          case 'LastUpdatedBy':
            return (
              <ImageCell
                title={createdByName}
                image={createdByAvatar}
                imageFallback={createdByAvatarFallback}
                key={column.id}
              />
            )
          case 'ServiceType':
            return (
              <TableCell
                align={column.align}
                key={column.id}
                testId={id + '-service-type'}
              >
                {serviceTypeText}
              </TableCell>
            )
          case 'Version':
            return (
              <TableCell
                testId="inventory-item-version"
                align={column.align}
                key={column.id}
              >
                {version}
              </TableCell>
            )
          case 'VersionCreated':
            return (
              <DateCell
                date={dateCreated}
                density={'Compact'}
                format={formatDate}
                testId="inventory-date-created"
                key={column.id}
              />
            )
          case 'Courtesy':
            return (
              <TableCell align={column.align} key={column.id}>
                {courtesyAmount}
              </TableCell>
            )
          case 'PriceType':
            return (
              <TableCell
                align={column.align}
                key={column.id}
                testId={id + '-price-type'}
              >
                {priceTypeText}
              </TableCell>
            )
          case 'SupportingItems':
            return (
              <TableCell
                align={column.align}
                key={column.id}
                testId={id + '-supporting-items'}
              >
                {supportingItemsCount}
              </TableCell>
            )
          case 'Price':
            return (
              <InventoryPriceCell
                price={clientPrice}
                column={column}
                key={column.id}
              />
            )
          case 'ProPrice':
            return (
              <InventoryPriceCell
                price={proPrice}
                column={column}
                key={column.id}
              />
            )
          case 'Actions':
            return (
              <TableCell
                className={styles.sticky}
                align={column.align}
                key={column.id}
              >
                <RowActions
                  override={override}
                  hasActions={hasActions}
                  canEdit={canEdit}
                  canOverride={canOverride}
                  canArchive={canArchive}
                  canUnarchive={canUnarchive}
                  hasWorkspace={hasWorkspace}
                  onEdit={onEdit}
                  onOverride={onOverride}
                  onArchive={onArchive}
                  onUnarchive={onUnarchive}
                  testIds={actionsTestIds}
                />
              </TableCell>
            )
          default:
            return <TableCell key={column.id} />
        }
      })}
    </TableRow>
  )
}

interface InventoryPriceCellProps {
  price: InventoryItemPriceDisplay
  column: InventoryRowColumn
}

function InventoryPriceCell({ price, column }: InventoryPriceCellProps) {
  if (price.type === 'Single') {
    return <TableCell align={column.align}>{price.text}</TableCell>
  }

  return (
    <ProgressionPricing progressions={price.progressions} column={column} />
  )
}

interface WorkItemProps {
  name: string
  shortId: string
  override: boolean
}

function WorkItemCell({ name, shortId, override }: WorkItemProps) {
  return (
    <TableCell>
      <div className={styles.workItem}>
        <span className={styles.name}>
          {name}
          {override && <span className={styles.override}> *</span>}
        </span>
        <span className={styles.description}>{shortId}</span>
      </div>
    </TableCell>
  )
}

interface ProgressionPricingProps {
  column: InventoryRowColumn
  progressions: InventoryItemProgressionPriceDisplay[]
}

function ProgressionPricing({ column, progressions }: ProgressionPricingProps) {
  return (
    <TableCell align={column.align}>
      <Dropdown
        noBottomPadding
        autoDismiss
        width="miniscule"
        alignContent="right"
        button={
          <IconButton
            name="progress"
            label="Show progression pricing"
            color="var(--grey600)"
          />
        }
      >
        {progressions.map((progression) => (
          <Fragment key={progression.name}>
            <DropdownTitle>{progression.name}</DropdownTitle>
            {progression.steps.map((step) => (
              <DropdownItem key={step.name}>
                {step.name} - {step.priceText}
              </DropdownItem>
            ))}
          </Fragment>
        ))}
      </Dropdown>
    </TableCell>
  )
}

interface RowActionsProps {
  override: boolean
  hasActions: boolean
  canEdit: boolean
  canOverride: boolean
  canArchive: boolean
  canUnarchive: boolean
  hasWorkspace: boolean
  onEdit: () => void
  onOverride: () => void
  onArchive: () => void
  onUnarchive: () => void
  testIds?: InventoryRowActionsTestIds
}

function RowActions({
  override,
  hasActions,
  canEdit,
  canOverride,
  canArchive,
  canUnarchive,
  hasWorkspace,
  onEdit,
  onOverride,
  onArchive,
  onUnarchive,
  testIds,
}: RowActionsProps) {
  if (!hasActions) {
    return null
  }

  return (
    <Dropdown
      testId={testIds?.menu}
      alignContent="right"
      autoDismiss
      position="bottom-end"
      button={<Icon name="more" />}
    >
      <DropdownTitle>Actions</DropdownTitle>
      {canEdit && (
        <DropdownItem onPress={onEdit} testId={testIds?.update}>
          <div className={styles.rowActionItem}>
            <Icon size={24} name="edit-outlined" />
            <span className={styles.rowActionItemText}>Edit Item</span>
          </div>
        </DropdownItem>
      )}
      {canOverride && (
        <DropdownItem onPress={onOverride} testId={testIds?.override}>
          <div className={styles.rowActionItem}>
            <Icon size={24} name="edit-outlined" />
            <span className={styles.rowActionItemText}>
              {hasWorkspace ? 'Override item' : 'New workspace override'}
            </span>
          </div>
        </DropdownItem>
      )}

      {canArchive && (
        <DropdownItem onPress={onArchive} testId={testIds?.archive}>
          <div className={styles.rowActionItem}>
            <Icon size={24} name="close" />
            <span className={styles.rowActionItemText}>
              Archive{override ? ' Override' : ' Item'}
            </span>
          </div>
        </DropdownItem>
      )}

      {canUnarchive && (
        <DropdownItem onPress={onUnarchive} testId={testIds?.unarchive}>
          <div className={styles.rowActionItem}>
            <Icon size={24} name="close" />
            <span className={styles.rowActionItemText}>
              Unarchive{override ? ' Override' : ' Item'}
            </span>
          </div>
        </DropdownItem>
      )}
    </Dropdown>
  )
}
